// Phase 10 slice 1 — Compliance Center.
//
// Three sections as tabs:
//   - State Reports: pick date range + state → opens HTML in new tab for print
//   - SDS Binder: list of products with sds_pdf_url, filterable by category
//   - Audubon Tracker: 6 category tracker with inline status dropdown + notes

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { Product, UserRole } from '@greeniq/shared';
import { ApiError, api } from '../api/client.js';
import { useMe } from '../context/MeContext.js';
import { PageHeader } from '../components/ui/page-header';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

const WRITE_ROLES: UserRole[] = ['ageye_admin', 'superintendent'];

const AUDUBON_LABELS: Record<string, string> = {
  environmental_planning: 'Environmental planning',
  wildlife_habitat_mgmt: 'Wildlife habitat management',
  chemical_safety_reduction: 'Chemical safety + reduction',
  water_quality_mgmt: 'Water quality management',
  water_conservation: 'Water conservation',
  outreach_education: 'Outreach + education',
};

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  not_started: { label: 'Not started', cls: 'bg-bg-elevated text-fg' },
  in_progress: { label: 'In progress', cls: 'bg-warning-muted text-[color:var(--color-warning)]' },
  complete: { label: 'Complete', cls: 'bg-success-muted text-[color:var(--color-success)]' },
};

export function CompliancePage() {
  return (
    <div className="max-w-6xl p-6 md:p-10">
      <PageHeader
        eyebrow="Phase 10"
        title="Compliance Center"
        description="State pesticide reports, OSHA SDS binder, Audubon Cooperative Sanctuary tracker."
      />

      <Tabs defaultValue="state" className="mt-8">
        <TabsList>
          <TabsTrigger value="state">State Reports</TabsTrigger>
          <TabsTrigger value="sds">SDS Binder</TabsTrigger>
          <TabsTrigger value="audubon">Audubon Tracker</TabsTrigger>
        </TabsList>
        <TabsContent value="state">
          <StateReportsTab />
        </TabsContent>
        <TabsContent value="sds">
          <SdsBinderTab />
        </TabsContent>
        <TabsContent value="audubon">
          <AudubonTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StateReportsTab() {
  const { courseSlug } = useParams();
  const [state, setState] = useState<'NC' | 'ID'>('ID');
  const now = new Date();
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [to, setTo] = useState(now.toISOString().slice(0, 10));

  const href = courseSlug
    ? `/api/courses/${courseSlug}/compliance/state-report?state=${state}&from=${from}&to=${to}`
    : '#';

  return (
    <div className="max-w-2xl">
      <p className="text-sm text-fg-muted">
        Generates a print-ready application log for state pesticide-report filings. Reads the application log; no edits are made.
      </p>
      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <label className="text-xs">
          State
          <select
            value={state}
            onChange={(e) => setState(e.target.value as 'NC' | 'ID')}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
          >
            <option value="ID">Idaho (ISDA)</option>
            <option value="NC">North Carolina (NCDA)</option>
          </select>
        </label>
        <label className="text-xs">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
          />
        </label>
        <label className="text-xs">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 w-full border border-border rounded-md py-2 px-2"
          />
        </label>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="mt-4 inline-block px-4 py-2 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400"
      >
        Open report →
      </a>
    </div>
  );
}

function SdsBinderTab() {
  const { courseSlug } = useParams();
  const [products, setProducts] = useState<Product[] | null>(null);
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseSlug) return;
    (async () => {
      try {
        const r = await api.get<{ products: Product[] }>(`/courses/${courseSlug}/compliance/sds`);
        setProducts(r.products);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
  }, [courseSlug]);

  if (error) return <div className="text-[color:var(--color-error)]">{error}</div>;
  if (!products) return <p className="text-fg-muted">Loading…</p>;

  const filtered = products.filter((p) =>
    !filter ? true : p.name.toLowerCase().includes(filter.toLowerCase())
      || (p.manufacturer ?? '').toLowerCase().includes(filter.toLowerCase()),
  );

  if (products.length === 0) {
    return (
      <div className="p-8 text-center text-fg-muted bg-bg-card rounded-lg border border-dashed border-border">
        <p>No products with SDS or label links in the catalog yet.</p>
        <p className="mt-2 text-sm">Populate via the bulk product import — the CSV schema includes sds_pdf_url and label_pdf_url columns.</p>
      </div>
    );
  }

  return (
    <div>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by product or manufacturer…"
        className="w-full max-w-md border border-border rounded-md py-2 px-3 text-sm"
      />
      <div className="mt-4 overflow-auto bg-bg-card rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-border bg-bg-elevated">
              <th className="px-3 py-2 font-semibold text-fg-muted">Product</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Category</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Signal</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">EPA</th>
              <th className="px-3 py-2 font-semibold text-fg-muted">Links</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2">
                  <div className="font-semibold">{p.name}</div>
                  <div className="text-xs text-fg-muted">{p.manufacturer ?? ''}</div>
                </td>
                <td className="px-3 py-2 capitalize">{p.category.replace(/_/g, ' ')}</td>
                <td className="px-3 py-2">{p.signalWord ?? '—'}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.epaRegNo ?? '—'}</td>
                <td className="px-3 py-2 text-xs">
                  {p.sdsPdfUrl && <a href={p.sdsPdfUrl} target="_blank" rel="noreferrer" className="text-greeniq-400 hover:underline mr-3">SDS →</a>}
                  {p.labelPdfUrl && <a href={p.labelPdfUrl} target="_blank" rel="noreferrer" className="text-greeniq-400 hover:underline">Label →</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface AudubonItem {
  category: string;
  status: 'not_started' | 'in_progress' | 'complete';
  notes: string | null;
  evidencePhotoId: number | null;
}

function AudubonTab() {
  const { courseSlug } = useParams();
  const { me } = useMe();
  const role: UserRole | undefined = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canEdit = role ? WRITE_ROLES.includes(role) : false;
  const [items, setItems] = useState<AudubonItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!courseSlug) return;
    try {
      const r = await api.get<{ items: AudubonItem[] }>(`/courses/${courseSlug}/compliance/audubon`);
      setItems(r.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  if (error) return <div className="text-[color:var(--color-error)]">{error}</div>;
  if (!items) return <p className="text-fg-muted">Loading…</p>;

  const complete = items.filter((i) => i.status === 'complete').length;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-fg-muted">
          Audubon Cooperative Sanctuary Program for Golf — 6 categories required for certification.
        </p>
        <span className="text-sm text-fg">
          <strong>{complete}</strong> / {items.length} complete
        </span>
      </div>
      <div className="mt-4 bg-bg-card rounded-lg border border-border divide-y divide-border">
        {items.map((item) => (
          <AudubonRow
            key={item.category}
            courseSlug={courseSlug ?? ''}
            item={item}
            canEdit={canEdit}
            onSaved={load}
          />
        ))}
      </div>
    </div>
  );
}

function AudubonRow({
  courseSlug,
  item,
  canEdit,
  onSaved,
}: {
  courseSlug: string;
  item: AudubonItem;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.notes ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.put(`/courses/${courseSlug}/compliance/audubon`, {
        category: item.category,
        status,
        notes: notes || null,
      });
      setEditing(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  const badge = STATUS_LABELS[item.status];

  return (
    <div className="p-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-fg">{AUDUBON_LABELS[item.category] ?? item.category}</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge?.cls}`}>
              {badge?.label}
            </span>
          </div>
          {!editing && item.notes && (
            <p className="mt-2 text-sm text-fg-muted whitespace-pre-wrap">{item.notes}</p>
          )}
        </div>
        {canEdit && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-xs text-greeniq-400 hover:underline"
          >
            Edit
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          <label className="text-xs">
            Status
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as AudubonItem['status'])}
              className="mt-1 w-full border border-border rounded-md py-2 px-2"
            >
              <option value="not_started">Not started</option>
              <option value="in_progress">In progress</option>
              <option value="complete">Complete</option>
            </select>
          </label>
          <label className="text-xs md:col-span-1">
            Notes
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="mt-1 w-full border border-border rounded-md py-2 px-2"
              placeholder="Evidence, progress notes, next steps…"
            />
          </label>
          <div className="md:col-span-2 flex items-center gap-2">
            <button
              onClick={() => void save()}
              disabled={saving}
              className="px-3 py-1.5 rounded-md bg-greeniq-500 text-[#0b1014] text-xs font-semibold hover:bg-greeniq-400 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-xs text-fg-muted hover:text-fg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
